import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError, resolveImageUrl } from "../../lib/adminApi";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminJournalList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/journal");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (e) => {
    if (!window.confirm(`¿Eliminar la nota "${e.title.replace("\n", " ")}"?`)) return;
    try {
      await adminApi.delete(`/journal/${e.id}`);
      toast.success("Nota eliminada");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  return (
    <section className="px-10 py-12" data-testid="admin-journal-list">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div
            className="text-[var(--pc-bronze)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 11,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
            }}
          >
            Notas
          </div>
          <h1
            className="headline-xl mt-3 text-[var(--pc-text)]"
            style={{ fontSize: "clamp(26px, 2.6vw, 38px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
          >
            Cuaderno
          </h1>
          <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />
        </div>

        <Link to="/admin/cuaderno/nuevo" className="admin-btn-primary" data-testid="new-journal-btn">
          <Plus size={14} className="inline mr-2" />
          Nueva nota
        </Link>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="mt-12 text-[var(--pc-text-soft)]" style={{ fontFamily: "Inter", fontSize: 13 }}>
          Aún no hay notas.
        </p>
      ) : (
        <div className="mt-10 border-t border-[var(--pc-line)]" data-testid="journal-rows">
          {items.map((e) => (
            <div
              key={e.id}
              className="grid grid-cols-12 items-center gap-5 border-b border-[var(--pc-line)] py-6"
              data-testid={`journal-row-${e.id}`}
            >
              <div className="col-span-2 md:col-span-1">
                {e.image ? (
                  <img
                    src={resolveImageUrl(e.image)}
                    alt=""
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square bg-[#15151a]" />
                )}
              </div>
              <div className="col-span-1">
                <span
                  className="text-[var(--pc-bronze)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                    fontSize: 22,
                  }}
                >
                  {e.number}
                </span>
              </div>
              <div className="col-span-6">
                <h3
                  className="text-[var(--pc-text)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                    fontSize: 16,
                    letterSpacing: "0.1em",
                    lineHeight: 1.4,
                    whiteSpace: "pre-line",
                  }}
                >
                  {e.title}
                </h3>
                <p
                  className="text-[var(--pc-text-soft)] mt-2"
                  style={{ fontFamily: "Inter", fontSize: 12, letterSpacing: "0.04em" }}
                >
                  {e.date}
                </p>
              </div>
              <div className="col-span-4 md:col-span-4 flex justify-end gap-2">
                <Link
                  to={`/admin/cuaderno/${e.id}`}
                  className="admin-btn-ghost flex items-center gap-2 text-xs"
                  data-testid={`edit-journal-${e.id}`}
                >
                  <Pencil size={12} /> Editar
                </Link>
                <button
                  onClick={() => onDelete(e)}
                  className="admin-btn-ghost flex items-center gap-2 text-xs text-red-300 hover:text-red-200"
                  data-testid={`delete-journal-${e.id}`}
                >
                  <Trash2 size={12} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
